import React, { useState, useMemo } from 'react';
import { Page, Content, Progress } from '@backstage/core-components';
import {
  Grid,
  Box,
  Container,
  Typography,
  Divider,
  Tooltip,
  IconButton,
  Paper,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ConstructionIcon from '@mui/icons-material/Construction';
import { AppCard } from '../AppCard';
import { FilterSection } from '../FilterSection';
import { useMarketplaceData } from '../../hooks/useMarketplaceData';
import { useHomepageProducts } from '../../hooks/useHomepageProducts';
import './HomePage.css';
import { Breadcrumbs } from '@internal/backstage-plugin-global-header';
import { ErrorToast, InlineError } from '@internal/backstage-plugin-global-header';

export interface HomePageProps {
  tabId?: string;
}

export const HomePage = ({ tabId = 'marketplace' }: HomePageProps) => {
  const [typeFilter, setTypeFilter] = useState('');
  const [productTagFilter, setProductTagFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { getUsageCount, usageError, clearUsageError } = useMarketplaceData();
  const { products: allApps, loading, error, refetch } = useHomepageProducts(tabId);

  const typeOptions = useMemo(() => {
    return [...new Set(allApps.map(app => app.type).filter(Boolean))];
  }, [allApps]);

  const teamOptions = useMemo(() => {
    return [...new Set(allApps.map(app => app.team).filter(Boolean))] as string[];
  }, [allApps]);

  const filteredApps = useMemo(() => {
    return allApps.filter(app => {
      if (typeFilter && app.type?.toLowerCase() !== typeFilter)
        return false;
      if (productTagFilter && app.team !== productTagFilter)
        return false;
      if (
        searchQuery &&
        !app.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !app.description.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !(app.type && app.type.toLowerCase().includes(searchQuery.toLowerCase())) &&
        !(app.team && app.team.toLowerCase().includes(searchQuery.toLowerCase()))
      )
        return false;
      return true;
    });
  }, [typeFilter, productTagFilter, searchQuery, allApps]);



  if (loading) {
    return <Progress />;
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'linear-gradient(to bottom right, #eff6ff, #e0e7ff, #faf5ff)' }}>
        <Page themeId="tool">
          <Content>
            <Breadcrumbs />
            <Box mt={2}>
              <InlineError
                title="Products could not be loaded"
                description="Products for this section are unavailable."
                onRetry={refetch}
              />
            </Box>
          </Content>
        </Page>
      </Box>
    );
  }

  if (allApps.length === 0) {
    return (
      <Page themeId="tool">
        <Content>
          <Breadcrumbs />
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="60vh"
          >
            <Paper
              elevation={0}
              sx={{
                padding: 4,
                textAlign: 'center',
                maxWidth: 500,
                borderRadius: 4,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              }}
            >
              <ConstructionIcon sx={{ fontSize: 80, color: '#1976d2', mb: 2 }} />
              <Typography variant="h4" gutterBottom>
                Work in Progress
              </Typography>
              <Typography variant="body1" color="text.secondary">
                This section is currently under development. Please check back later.
              </Typography>
            </Paper>
          </Box>
        </Content>
      </Page>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background:
          'linear-gradient(to bottom right, #eff6ff, #e0e7ff, #faf5ff)',
      }}
    >
      <Page themeId="tool">
        <Content>
          <Breadcrumbs />
          <Container
            maxWidth={false}
            disableGutters
            sx={{ px: { xs: 1, sm: 2, md: 4 }, py: 2 }}
          >
            <FilterSection
              typeFilter={typeFilter}
              productTagFilter={productTagFilter}
              searchQuery={searchQuery}
              filteredCount={filteredApps.length}
              totalCount={allApps.length}
              typeOptions={typeOptions}
              teamOptions={teamOptions}
              onTypeFilterChange={setTypeFilter}
              onProductTagFilterChange={setProductTagFilter}
              onSearchChange={setSearchQuery}
            />

            {filteredApps.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 3 }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#3d5a80',
                      mr: 1,
                      marginBottom: 0,
                    }}
                  >
                    Assets
                  </Typography>
                  <Tooltip
                    title="Reusable services, components, and fully built end-user-facing applications designed by and for teams across Epsilon."
                    arrow
                    placement="right"
                  >
                    <IconButton size="small" sx={{ mr: 1 }}>
                      <InfoOutlinedIcon
                        sx={{ fontSize: 18, color: '#3d5a80' }}
                      />
                    </IconButton>
                  </Tooltip>
                  <Divider
                    sx={{ flex: 1, borderColor: '#3d5a80', opacity: 0.3 }}
                  />
                </Box>
                <Grid
                  container
                  spacing={2}
                  alignItems="stretch"
                  sx={{ width: '100%' }}
                >
                  {filteredApps.map((app, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      sx={{ display: 'flex' }}
                      key={`asset-${index}`}
                    >
                      <AppCard
                        {...app}
                        usageCount={
                          app.product_key
                            ? getUsageCount(app.product_key)
                            : undefined
                        }
                        is_plugin={app.is_plugin}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Container>
        </Content>
      </Page>
      <ErrorToast message={usageError} onClose={clearUsageError} />
    </Box>
  );
};
